import type { JSX } from "react";
import { Link } from "react-router";
import { toEpochSeconds } from "../../domain/time";
import { routePaths } from "../../app/navigation";
import { useI18n } from "../../shared/i18n/I18nContext";
import { cx } from "../../shared/ui/classNames";
import { formatLocalDate } from "../../shared/ui/format";
import styles from "../../shared/ui/ui.module.css";
import { nextOwnShift, timeRange } from "../roster/myRosterModel";
import { useMyRoster } from "../roster/useMyRoster";
import clockStyles from "./ClockPage.module.css";

/** Properties of {@link NextShift}. */
export interface NextShiftProps {
  /** Now in milliseconds (device time corrected by the server offset). */
  readonly nowMs: number;
}

/**
 * The next own published shift on the clock screen (T-178): information only, it never records,
 * starts or ends anything (`SINV-01`); offline it comes from the roster kept on the device. Nothing
 * is shown while rostering is off or no shift lies ahead.
 *
 * @param props - Component properties.
 * @returns The line, or `null`.
 */
export function NextShift(props: NextShiftProps): JSX.Element | null {
  const { t, languageTag } = useI18n();
  const { query: mine, enabled } = useMyRoster();
  const next =
    enabled && mine.data !== undefined
      ? nextOwnShift(mine.data.roster, toEpochSeconds(props.nowMs))
      : null;

  if (next === null) {
    return null;
  }

  const line = (
    <p className={cx(styles["muted"], clockStyles["footnote"])}>
      {t("clock.nextShift", {
        date: formatLocalDate(next.date, languageTag),
        times: timeRange(next),
      })}{" "}
      <Link to={routePaths.myRoster}>{t("clock.myRosterLink")}</Link>
    </p>
  );

  return line;
}
